import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import ChatWidget from "@/components/ChatWidget";
import { VILOGI_URL } from "@/lib/constants";

const NAV = [
  { to: "/", label: "Accueil", end: true },
  { to: "/notre-agence", label: "Notre agence" },
  { to: "/metiers/syndic", label: "Nos métiers", match: "/metiers" },
  { to: "/actualites", label: "Actualités" },
  { to: "/faq", label: "FAQ" },
  { to: "/lexique", label: "Lexique" },
  { to: "/contact", label: "Contact" },
];

const METIERS = [
  { to: "/metiers/syndic", label: "Syndic de copropriété" },
  { to: "/metiers/syndic-en-ligne", label: "Syndic en ligne" },
  { to: "/metiers/gestionnaire-asl", label: "Gestionnaire ASL" },
  { to: "/metiers/expertise", label: "Expertise" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [metiersOpen, setMetiersOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0F172A] shadow-lg"
          : "bg-[#0F172A]/95 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[96px] lg:h-[144px]">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-4 group">
            <img
              src="/assets/logo-cabinet-gpi.png"
              alt="Cabinet GPI Immobilier"
              className="h-20 sm:h-24 lg:h-32 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-10" data-testid="primary-nav">
            {NAV.map((item) => {
              if (item.label === "Nos métiers") {
                return (
                  <div
                    key={item.to}
                    className="relative"
                    onMouseEnter={() => setMetiersOpen(true)}
                    onMouseLeave={() => setMetiersOpen(false)}
                  >
                    <NavLink
                      to={item.to}
                      data-testid="nav-metiers"
                      className={({ isActive }) =>
                        `gpi-link text-base font-bold tracking-wide ${
                          loc.pathname.startsWith("/metiers") || isActive
                            ? "text-[#D4B584]"
                            : "text-white"
                        }`
                      }
                    >
                      Nos métiers
                    </NavLink>
                    {metiersOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                        <div className="bg-[#0F172A] border border-[#B8935A]/40 shadow-xl min-w-[280px] py-2">
                          {METIERS.map((m) => (
                            <NavLink
                              key={m.to}
                              to={m.to}
                              data-testid={`nav-metier-${m.to.split("/").pop()}`}
                              className="block px-5 py-3 text-sm font-semibold text-white hover:bg-[#1E293B] hover:text-[#D4B584] transition-colors"
                            >
                              {m.label}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `gpi-link text-base font-bold tracking-wide ${
                      isActive ? "text-[#D4B584]" : "text-white"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="hidden lg:block">
            <a
              href={VILOGI_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-espace-client-btn"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#D4B584] text-[#D4B584] hover:bg-[#D4B584] hover:text-[#0F172A] text-base font-bold tracking-wide transition-all duration-300"
            >
              Espace copropriétaire
              <ArrowUpRight size={16} />
            </a>
          </div>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
            aria-label="Menu"
          >
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-[#0F172A] border-t border-white/10" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `block py-2 text-lg font-bold ${isActive ? "text-[#D4B584]" : "text-white"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pl-4 border-l border-[#D4B584]/40 space-y-2">
              {METIERS.map((m) => (
                <NavLink
                  key={m.to}
                  to={m.to}
                  className="block py-1 text-base font-semibold text-[#CBD5E1]"
                >
                  → {m.label}
                </NavLink>
              ))}
            </div>
            <a
              href={VILOGI_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              data-testid="mobile-espace-client-btn"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 border-2 border-[#D4B584] text-[#D4B584] text-base font-bold tracking-wide"
            >
              Espace copropriétaire
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#0F172A] text-[#F9F8F6] mt-32" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
        <div className="sm:col-span-2">
          <img
            src="/assets/logo-cabinet-gpi.png"
            alt="Cabinet GPI Immobilier"
            className="h-32 w-auto object-contain mb-6"
          />
          <p className="text-[#94A3B8] text-sm leading-relaxed max-w-md">
            Syndic de copropriété, gestionnaire ASL et expert judiciaire.
            Un accompagnement rigoureux et personnalisé pour votre patrimoine immobilier.
          </p>
        </div>

        <div>
          <h4 className="font-overline-lg text-[#B8935A] mb-5">Métiers</h4>
          <ul className="space-y-3 text-sm">
            {METIERS.map((m) => (
              <li key={m.to}>
                <Link to={m.to} className="text-[#CBD5E1] hover:text-white transition-colors">
                  {m.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-overline-lg text-[#B8935A] mb-5">Contact</h4>
          <ul className="space-y-3 text-sm text-[#CBD5E1]">
            <li>34 rue Saint-Bruno</li>
            <li>13004 Marseille</li>
            <li>06 87 45 32 31</li>
            <li>contact@cabinetgpi-immobilier.fr</li>
            <li className="pt-2">
              <Link to="/contact" className="text-[#B8935A] hover:underline">
                Nous écrire →
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-[#64748B]">
        <div>© {new Date().getFullYear()} Cabinet GPI. Tous droits réservés.</div>
        <div className="flex gap-6">
          <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-[#B8935A]">
            Espace administrateur
          </Link>
          <span>Mentions légales</span>
        </div>
      </div>
    </div>
  </footer>
);

export default function Layout() {
  return (
    <>
      <Header />
      <main className="pt-[96px] lg:pt-[144px]">
        <Outlet />
      </main>
      <Footer />
      <ChatWidget />
    </>
  );
}
